import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Lightbulb, Target, Zap, Code2, Users, ChefHat, Sparkles, Brain, Rocket, Heart, Eye, Clock, ShoppingBag, Globe, Award, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VideoModal from "@/components/VideoModal";
import SEOHead from "@/components/SEOHead";
import { PAGE_SEO_CONFIG } from "@/utils/seoUtils";
import { useRef, useState } from "react";

const AboutUs = () => {
  const containerRef = useRef(null);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <div className="min-h-screen">
      <SEOHead
        title={PAGE_SEO_CONFIG.about.title}
        description={PAGE_SEO_CONFIG.about.description}
        keywords={PAGE_SEO_CONFIG.about.keywords?.join(', ')}
      />
      <Header />
      
      {/* Hero Section with Parallax */}
      <section className="relative pt-24 pb-12 px-4 overflow-hidden bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]" ref={containerRef}>
        {/* Animated Background Elements */}
        <motion.div
          style={{ y }}
          className="absolute inset-0 overflow-hidden"
        >
          <div className="absolute top-20 left-10 w-96 h-96 bg-yellow-400/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        </motion.div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm px-6 py-3 rounded-full mb-8"
            >
              <Sparkles className="h-5 w-5 text-yellow-400" />
              <span className="text-yellow-400 font-semibold">Our Story</span>
            </motion.div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              From a Dim Restaurant
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                To Bright Ideas
              </span>
            </h1>
            <motion.p 
              style={{ opacity }}
              className="text-lg md:text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed"
            >
              How squinting at menus led to revolutionizing the entire dining experience
            </motion.p>
          </motion.div>

          {/* Visual Story Preview */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="relative"
          >
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-all">
                <Eye className="h-12 w-12 text-red-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">The Struggle</h3>
                <p className="text-blue-100">Can't read menus in dim lighting</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-all">
                <Brain className="h-12 w-12 text-yellow-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">The Insight</h3>
                <p className="text-blue-100">AI can personalize every meal</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-all">
                <Rocket className="h-12 w-12 text-green-400 mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">The Solution</h3>
                <p className="text-blue-100">Dine AI was born</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Journey - Interactive Timeline */}
      <section className="py-12 px-4 relative overflow-hidden bg-gradient-to-b from-[#2256e5] to-[#1f4ed1]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              The Journey to <span className="text-yellow-400">Dine AI</span>
            </h2>
            <p className="text-lg text-blue-100">Every great innovation starts with frustration</p>
          </motion.div>

          {/* Timeline with Images */}
          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-yellow-400/20 via-yellow-400/50 to-yellow-400/20 hidden md:block" />
            
            {/* Timeline Items */}
            <div className="space-y-16">
              {/* Problem 1 */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row items-center gap-8"
              >
                <div className="flex-1 text-right">
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 inline-block text-left md:text-right">
                    <div className="flex items-center gap-3 mb-4 justify-end">
                      <h3 className="text-xl font-bold text-white">The Dark Restaurant</h3>
                      <Eye className="h-8 w-8 text-red-400" />
                    </div>
                    <p className="text-blue-100 mb-2">
                      Another dinner, another struggle. Holding my phone flashlight over the menu, squinting at tiny text in cursive fonts.
                    </p>
                    <p className="text-yellow-400 font-semibold">"Why is reading a menu this hard?"</p>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center z-10 relative">
                    <span className="text-black font-bold">1</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="rounded-2xl overflow-hidden h-48">
                    <img 
                      src="/about-pain-point-1.png" 
                      alt="Dimly lit restaurant menu"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Problem 2 */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row-reverse items-center gap-8"
              >
                <div className="flex-1 text-left">
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 inline-block">
                    <div className="flex items-center gap-3 mb-4">
                      <Clock className="h-8 w-8 text-orange-400" />
                      <h3 className="text-xl font-bold text-white">The Endless Wait</h3>
                    </div>
                    <p className="text-blue-100 mb-2">
                      20 minutes. Still no waiter. My friends and I already know what we want, but we're stuck waiting.
                    </p>
                    <p className="text-yellow-400 font-semibold">"There has to be a better way!"</p>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center z-10 relative">
                    <span className="text-black font-bold">2</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="rounded-2xl overflow-hidden h-48">
                    <img 
                      src="/about-pain-point-2.png" 
                      alt="Busy restaurant with customers waiting"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Problem 3 - Language Barriers */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="flex flex-col md:flex-row items-center gap-8"
              >
                <div className="flex-1 text-right">
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 inline-block text-left md:text-right">
                    <div className="flex items-center gap-3 mb-4 justify-end">
                      <h3 className="text-xl font-bold text-white">Lost in Translation</h3>
                      <Globe className="h-8 w-8 text-purple-400" />
                    </div>
                    <p className="text-blue-100 mb-2">
                      Beautiful French bistro. Authentic menu. But "Côtelettes d'Agneau à la Provençale" tells me nothing about what I'm ordering.
                    </p>
                    <p className="text-yellow-400 font-semibold">"Why can't menus speak my language?"</p>
                  </div>
                </div>
                <div className="relative">
                  <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center z-10 relative">
                    <span className="text-black font-bold">3</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="rounded-2xl overflow-hidden h-48">
                    <img 
                      src="/about-pain-point-3.png" 
                      alt="Complex menu item descriptions"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </motion.div>

              {/* The Lightbulb Moment - Clean Integrated Design */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative mt-12"
              >
                {/* Content Box with Integrated Lightbulb */}
                <div className="bg-gradient-to-r from-yellow-400/10 via-orange-400/10 to-yellow-400/10 backdrop-blur-md rounded-3xl border border-yellow-400/30 p-12 max-w-4xl mx-auto">
                  <h3 className="text-2xl md:text-3xl font-bold text-center mb-6 flex items-center justify-center gap-4">
                    <motion.div
                      initial={{ rotate: -45, scale: 0 }}
                      whileInView={{ rotate: 0, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center shadow-lg shadow-yellow-400/50"
                    >
                      <Lightbulb className="h-8 w-8 text-white" />
                    </motion.div>
                    <span>
                      <span className="text-white">The </span>
                      <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">"Aha!"</span>
                      <span className="text-white"> Moment</span>
                    </span>
                  </h3>
                  
                  <div className="text-center">
                    <p className="text-lg text-white mb-4 leading-relaxed max-w-3xl mx-auto">
                      "As CEO of two AI companies, I've seen AI transform industries. 
                      Netflix knows what show I'll love. Spotify creates perfect playlists. 
                      Sitting there in that dim restaurant, struggling with another menu, it hit me:
                    </p>
                    <p className="text-xl font-bold text-yellow-400 mb-6">
                      Why can't restaurants know what I want to eat?"
                    </p>
                    <p className="text-base text-blue-100 max-w-2xl mx-auto">
                      That night, the vision for Dine AI was born – making every menu personal, readable, and intelligent.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* The Vision - Full Width Interactive */}
      <section className="py-12 px-4 bg-gradient-to-b from-[#1f4ed1] to-[#2256e5] relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <motion.div
            animate={{ 
              x: [0, 100, 0],
              y: [0, -100, 0],
            }}
            transition={{ duration: 20, repeat: Infinity }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-white/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ 
              x: [0, -100, 0],
              y: [0, 100, 0],
            }}
            transition={{ duration: 15, repeat: Infinity }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Imagine <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">Dining Reimagined</span>
            </h2>
          </motion.div>

          {/* Krishna's Story - Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative max-w-5xl mx-auto"
          >
            <div className="bg-gradient-to-r from-yellow-400/10 via-orange-400/10 to-red-400/10 backdrop-blur-md rounded-3xl p-1">
              <div className="bg-gray-900/90 rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Meet Krishna</h3>
                    <div className="space-y-4">
                      <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="flex items-center gap-4"
                      >
                        <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                          <span className="text-black font-bold">1</span>
                        </div>
                        <p className="text-blue-100">Walks into any restaurant</p>
                      </motion.div>
                      <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="flex items-center gap-4"
                      >
                        <div className="w-12 h-12 bg-orange-400 rounded-full flex items-center justify-center">
                          <span className="text-black font-bold">2</span>
                        </div>
                        <p className="text-blue-100">Opens Dine AI</p>
                      </motion.div>
                      <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.6 }}
                        className="flex items-center gap-4"
                      >
                        <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">3</span>
                        </div>
                        <p className="text-blue-100">Sees personalized recommendations</p>
                      </motion.div>
                    </div>
                  </div>
                  
                  {/* Phone Mockup */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="relative"
                  >
                    <div className="bg-gray-800 rounded-3xl p-4 shadow-2xl">
                      <div className="bg-gray-900 rounded-2xl p-6">
                        <div className="text-center mb-4">
                          <p className="text-sm text-gray-400">Welcome back, Krishna!</p>
                          <p className="text-lg font-semibold text-white">Your Personalized Menu</p>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-yellow-400/20 rounded-lg p-3 border border-yellow-400/30">
                            <p className="text-sm text-yellow-400 font-semibold">🥃 Start with</p>
                            <p className="text-white">Old Fashioned (Bourbon)</p>
                          </div>
                          <div className="bg-orange-400/20 rounded-lg p-3 border border-orange-400/30">
                            <p className="text-sm text-orange-400 font-semibold">🍗 Main Course</p>
                            <p className="text-white">Nashville Hot Chicken</p>
                          </div>
                          <div className="bg-red-400/20 rounded-lg p-3 border border-red-400/30">
                            <p className="text-sm text-red-400 font-semibold">🍰 Don't Skip</p>
                            <p className="text-white">Carrot Cake</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Glow Effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-orange-400/20 blur-3xl -z-10" />
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Key Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6 mt-16"
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl mb-4">
                <Brain className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">AI That Learns</h3>
              <p className="text-blue-100">Every meal teaches Dine AI more about your preferences</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-400 to-red-400 rounded-2xl mb-4">
                <Globe className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Works Everywhere</h3>
              <p className="text-blue-100">Your preferences travel with you to any restaurant</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-red-400 to-purple-400 rounded-2xl mb-4">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Personal Touch</h3>
              <p className="text-blue-100">Feel like every restaurant knows you personally</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The Team - Creative Layout */}
      <section className="py-12 px-4 relative overflow-hidden bg-gradient-to-b from-[#2256e5] to-[#1f4ed1]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Built by <span className="text-yellow-400">Innovators</span>
            </h2>
            <p className="text-lg text-blue-100">For restaurants and diners who deserve better</p>
          </motion.div>

          {/* Founder Card - Hero Style */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto mb-16"
          >
            <div className="bg-gradient-to-r from-yellow-400/20 to-orange-400/20 backdrop-blur-md rounded-3xl p-1">
              <div className="bg-gray-900/90 rounded-3xl p-8 md:p-12">
                <div className="grid md:grid-cols-3 gap-8 items-center">
                  <div className="md:col-span-1">
                    <div className="w-48 h-48 mx-auto bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full p-1">
                      <div className="w-full h-full bg-gray-800 rounded-full flex items-center justify-center">
                        <p className="text-gray-400 italic">[Founder Photo]</p>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <h3 className="text-2xl font-bold text-white mb-2">Krishna Kallakuri</h3>
                    <p className="text-yellow-400 text-lg mb-4">Founder & CEO</p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Award className="h-5 w-5 text-yellow-400" />
                        <p className="text-blue-100">CEO of DataFactZ & Diwo - Enterprise AI Leaders</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-yellow-400" />
                        <p className="text-blue-100">20+ years transforming industries with data & AI</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Heart className="h-5 w-5 text-yellow-400" />
                        <p className="text-blue-100">Passionate about making dining personal again</p>
                      </div>
                    </div>
                    <p className="text-blue-100 mt-6 italic">
                      "I believe every meal should be perfect. Not by chance, but by design."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Team Philosophy */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-8"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center"
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center mb-6">
                <Rocket className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Startup Speed</h3>
              <p className="text-blue-100">
                No committees. No red tape. Just rapid innovation based on real feedback.
              </p>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center"
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center mb-6">
                <Code2 className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">AI-Powered Development</h3>
              <p className="text-blue-100">
                We use AI to build AI. Cutting-edge tools help us move at lightning speed.
              </p>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center"
            >
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-pink-400 to-red-400 rounded-full flex items-center justify-center mb-6">
                <Users className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Growing Community</h3>
              <p className="text-blue-100">
                Every restaurant and diner joins a movement to make dining better for everyone.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action - Full Width Impact */}
      <section className="py-20 px-4 relative overflow-hidden bg-gradient-to-b from-[#1f4ed1] to-[#2256e5]">
        {/* Background Animation */}
        <div className="absolute inset-0">
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{ duration: 20, repeat: Infinity }}
            className="absolute top-1/2 left-1/2 w-[800px] h-[800px] -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-yellow-400/10 via-transparent to-orange-400/10 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto max-w-6xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-yellow-400/20 backdrop-blur-sm px-6 py-3 rounded-full mb-8"
            >
              <Sparkles className="h-5 w-5 text-yellow-400" />
              <span className="text-yellow-400 font-semibold">Join the Revolution</span>
            </motion.div>
            
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
              Your Next Chapter Starts
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                With One Click
              </span>
            </h2>
            <p className="text-lg md:text-xl text-blue-100 mb-12 max-w-3xl mx-auto">
              Whether you're a restaurant ready to boost revenue or a diner seeking perfect meals, 
              your journey to personalized dining begins now.
            </p>
            
            {/* Two CTAs - Restaurant and Diner */}
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20"
              >
                <ChefHat className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-4">For Restaurants</h3>
                <p className="text-blue-100 mb-6">
                  Transform your menu into a revenue engine with AI-powered insights
                </p>
                <Link to="/restaurant-menu-search">
                  <Button size="lg" className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 text-black hover:from-yellow-500 hover:to-orange-500 font-semibold">
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </motion.div>
              
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20"
              >
                <Heart className="h-12 w-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-4">For Diners</h3>
                <p className="text-blue-100 mb-6">
                  Experience dining that knows you as well as your best friend
                </p>
                <Button
                  size="lg"
                  className="w-full bg-white/20 hover:bg-white/30 text-white border border-white/30 font-semibold"
                  onClick={() => setIsVideoOpen(true)}
                >
                  Watch Demo
                  <Eye className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </div>
            
            {/* Stats to build trust */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8"
            >
              <div>
                <p className="text-4xl font-bold text-yellow-400">15-30%</p>
                <p className="text-blue-100">Revenue Increase</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-orange-400">20%</p>
                <p className="text-blue-100">Faster Table Turns</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-red-400">98.7%</p>
                <p className="text-blue-100">Menu Accuracy</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-purple-400">100%</p>
                <p className="text-blue-100">Dedicated Support</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl="https://youtube.com/shorts/l3xW3Uv0iGM?feature=share"
        title="Dine AI Demo - Transform Your Restaurant"
        format="mobile"
      />
    </div>
  );
};

export default AboutUs;